import i18n from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { resources, ns, defaultNS } from "./resources";

export const SUPPORTED_LANGS = ["en", "de"] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];

/** localStorage key the language toggle persists to (also read on next visit). */
export const LANG_STORAGE_KEY = "fd.lang";

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    ns: [...ns],
    defaultNS,
    fallbackLng: "en",
    supportedLngs: [...SUPPORTED_LANGS],
    // Treat "de-DE", "de-AT" etc. as "de" rather than falling back to English.
    nonExplicitSupportedLngs: true,
    load: "languageOnly",
    detection: {
      // Remembered choice wins; otherwise follow the browser. Persist the pick.
      order: ["localStorage", "navigator"],
      lookupLocalStorage: LANG_STORAGE_KEY,
      caches: ["localStorage"],
    },
    interpolation: { escapeValue: false }, // React already escapes
    react: { useSuspense: false }, // resources are bundled synchronously
  });

/** Keep <html lang> in sync for a11y/SEO. index.html ships lang="en" as the
 * pre-hydration default; this flips it once i18n resolves and on every toggle. */
function syncHtmlLang(lng: string | undefined): void {
  if (typeof document === "undefined") return;
  document.documentElement.lang = (lng || "en").split("-")[0]!;
}
syncHtmlLang(i18n.resolvedLanguage);
i18n.on("languageChanged", syncHtmlLang);
i18n.on("initialized", () => syncHtmlLang(i18n.resolvedLanguage));

// ── Legal-placeholder handling ────────────────────────────────────────────────
// Contractual strings live in the `legal` namespace with their German value set
// to the verbatim English prefixed by this sentinel. We strip it before display
// (users see clean English, never the token) and warn once per key in dev so an
// un-translated legal string is visible during QA. A legal string ships as
// German only when a reviewer deliberately removes the sentinel — see the legal
// guard test. Keep this token in sync with i18n/locales/de/legal.json.
export const LEGAL_SENTINEL = "[LEGAL-EN]";
const warnedLegal = new Set<string>();

export function stripLegalSentinel(key: string, value: string): string {
  if (value.startsWith(LEGAL_SENTINEL)) {
    if (import.meta.env.DEV && !warnedLegal.has(key)) {
      warnedLegal.add(key);
      // eslint-disable-next-line no-console
      console.warn(
        `[i18n] legal string "${key}" is an un-translated EN placeholder awaiting legal sign-off`,
      );
    }
    return value.slice(LEGAL_SENTINEL.length).trimStart();
  }
  return value;
}

/** Hook for the `legal` namespace: returns clean (sentinel-stripped) text and
 * re-renders on language change like any other `t`. */
export function useLegal(): (key: string, opts?: Record<string, unknown>) => string {
  const { t } = useTranslation("legal");
  return (key, opts) => stripLegalSentinel(key, t(key as never, opts) as string);
}

export default i18n;
